import type { MagnetSize } from '../simulation/Magnet.ts';
import type { Phase } from '../simulation/World.ts';

/** Buttons. Kept apart from HUD so read-only display and input never tangle. */
export interface ControlCallbacks {
  /** The primary button: start a run, or abandon one that is going.  */
  onRun(): void;
  /** Back to planning with the magnets left in place. */
  onAdjust(): void;
  /** Back to planning with the board cleared. */
  onClear(): void;
  onNext(): void;
  onStrength(size: MagnetSize): void;
  onToggleMute(): void;
}

export class Controls {
  private readonly runButton: HTMLButtonElement;
  private readonly clearButton: HTMLButtonElement;
  private readonly strengthPicker: HTMLElement;
  private readonly muteButton: HTMLButtonElement;
  private readonly nextButton: HTMLButtonElement;

  /** Same reasoning as HUD: the loop calls setPhase every frame. */
  private lastPhase: string | null = null;

  constructor(callbacks: ControlCallbacks, root: ParentNode = document) {
    this.clearButton = requireButton(root, '#btn-clear');
    this.runButton = requireButton(root, '#btn-run');
    this.strengthPicker = requireElement(root, '#strength-picker');

    this.muteButton = requireButton(root, '#btn-mute');
    this.nextButton = requireButton(root, '#btn-next');

    this.clearButton.addEventListener('click', () => callbacks.onClear());
    this.runButton.addEventListener('click', () => callbacks.onRun());
    this.muteButton.addEventListener('click', () => callbacks.onToggleMute());
    this.nextButton.addEventListener('click', () => callbacks.onNext());
    requireButton(root, '#btn-adjust').addEventListener('click', () => callbacks.onAdjust());

    for (const button of this.strengthPicker.querySelectorAll<HTMLButtonElement>('.seg')) {
      button.addEventListener('click', () => {
        for (const sibling of this.strengthPicker.querySelectorAll('.seg')) {
          sibling.classList.toggle('is-active', sibling === button);
        }
        callbacks.onStrength((button.dataset.strength ?? 'M') as MagnetSize);
      });
    }
  }

  /**
   * Strength selection is hidden by default. The first prototype gives every magnet the
   * same strength so that place / move / flip / remove is the only thing under test —
   * one variable at a time. `?strength=1` brings the picker back.
   */
  setStrengthPickerVisible(visible: boolean): void {
    this.strengthPicker.hidden = !visible;
  }

  setMuted(muted: boolean): void {
    this.muteButton.setAttribute('aria-pressed', String(muted));
    this.muteButton.setAttribute('aria-label', muted ? 'Unmute sound' : 'Mute sound');
  }

  /** Hide the next-level button on the last level. */
  setHasNextLevel(hasNext: boolean): void {
    this.nextButton.hidden = !hasNext;
  }

  /**
   * The run button carries the phase. Planning offers Start, and a run in progress offers
   * the way out of it; once the level is decided the outcome card owns the next move, so
   * the button steps back rather than competing with it.
   */
  setPhase(phase: Phase, canStart: boolean): void {
    const key = `${phase}|${canStart}`;
    if (key === this.lastPhase) return;
    this.lastPhase = key;

    const running = phase === 'running';
    this.runButton.textContent = running ? 'Stop' : 'Start';
    this.runButton.classList.toggle('is-running', running);
    this.runButton.disabled = phase === 'planning' ? !canStart : phase !== 'running';
    this.clearButton.disabled = phase !== 'planning';
  }
}

function requireElement(root: ParentNode, selector: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) throw new Error(`Missing UI element: ${selector}`);
  return element;
}

function requireButton(root: ParentNode, selector: string): HTMLButtonElement {
  return requireElement(root, selector) as HTMLButtonElement;
}
